<template>
    <div
        class="text-left font-light whitespace-nowrap h-10.25"
        :class="[backgroundClass, textColorClass, borderTopClass, borderBottomClass]"
    >
        <div class="flex flex-row items-center gap-2 h-full pl-2 pr-1" :class="[borderRightClass]">
            <div v-if="props.checkboxStatus !== 'No-Checkbox'" class="flex">
                <Tooltip>
                    <template #trigger="tooltipProps">
                        <input
                            ref="checkBoxRef"
                            :aria-labelledby="labelId"
                            :aria-describedby="tooltipProps['aria-labelledby']"
                            :popovertarget="tooltipProps['popovertarget']"
                            type="checkbox"
                            :disabled="
                                props.checkboxStatus === 'Disabled-Checked' ||
                                props.checkboxStatus === 'Disabled-Not-Checked'
                            "
                            :checked="props.checkboxStatus === 'Checked' || props.checkboxStatus === 'Disabled-Checked'"
                            class="size-4.5 rounded-sm border-gray-300 text-sky-700 focus:ring-sky-700"
                            :class="[
                                props.checkboxStatus === 'Disabled-Checked' ||
                                props.checkboxStatus === 'Disabled-Not-Checked'
                                    ? 'cursor-not-allowed'
                                    : 'cursor-pointer',
                                {
                                    'disabled:bg-gray-400 hover:text-gray-400':
                                        props.checkboxStatus === 'Disabled-Checked',
                                    'disabled:bg-gray-200 hover:text-gray-200':
                                        props.checkboxStatus === 'Disabled-Not-Checked',
                                },
                            ]"
                            @change="$emit('toggle-checkbox')"
                        />
                    </template>

                    <template #text>{{ props.checkboxTooltip }}</template>
                </Tooltip>
            </div>

            <div
                class="flex-1 flex flex-row items-center gap-2 h-full"
                @click="$emit('toggleLocationExpanded')"
                @keydown="$emit('keyPress', $event)"
            >
                <Tooltip v-if="props.checkboxStatus === 'No-Checkbox'">
                    <template #trigger="tooltipProps">
                        <TerrainIcon
                            class="size-4.5 shrink-0 flex items-center justify-center rounded-xs cursor-help focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:z-40 focus-visible:outline-gray-700"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        />
                    </template>

                    <template #text>
                        {{ $t('site-icon-alt') }}
                    </template>
                </Tooltip>

                <span class="flex items-center gap-2 tabular-nums text-xs">
                    <span :id="labelId" class="uppercase">
                        {{ props.label }}
                    </span>

                    <Tooltip v-if="props.errorMessage">
                        <template #trigger="tooltipProps">
                            <WarningIcon class="size-5" v-bind="tooltipProps" :tabindex="0" />
                        </template>

                        <template #text>
                            <div class="w-max">{{ props.errorMessage }}</div>
                        </template>
                    </Tooltip>
                </span>
            </div>

            <Tooltip v-if="showResolvedConflictsIcon">
                <template #trigger="tooltipProps">
                    <div class="flex items-center">
                        <FlashOffIcon class="text-gray-500" v-bind="tooltipProps" :tabindex="0" />
                    </div>
                </template>

                <template #text>
                    <i18n path="all-conflicts-resolved-tooltip" :args="{ count: props.conflictsCount.total }">
                        <template #conflictCount>
                            <span class="font-semibold">
                                {{ props.conflictsCount.total }}
                            </span>
                        </template>
                    </i18n>
                </template>
            </Tooltip>

            <Tooltip v-if="hasUnresolvedConflicts">
                <template #trigger="tooltipProps">
                    <div
                        class="flex flex-row gap-0.5 h-4 text-xs items-center ml-3 text-red-700 font-semibold tabular-nums cursor-help focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs"
                        v-bind="tooltipProps"
                        :tabindex="0"
                    >
                        <span>
                            {{ props.conflictsCount.unresolved }}
                        </span>

                        <FlashIcon class="size-4" />
                    </div>
                </template>

                <template #text>
                    <i18n path="unresolved-conflicts-tooltip" :args="{ count: props.conflictsCount.unresolved }">
                        <template #unresolvedCount>
                            <span class="font-semibold">
                                {{ props.conflictsCount.unresolved }}
                            </span>
                        </template>

                        <template #totalCount>
                            <span class="tabular-nums">
                                {{ props.conflictsCount.total }}
                            </span>
                        </template>
                    </i18n>
                </template>
            </Tooltip>

            <Tooltip v-if="props.linkTarget">
                <template #trigger="tooltipProps">
                    <RouterLink
                        v-bind="tooltipProps"
                        target="_blank"
                        :to="props.linkTarget"
                        class="border size-7 flex justify-center items-center rounded-sm cursor-pointer focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-700 focus-visible:z-40"
                        :class="[
                            locationLinkBackgroundColorClasses,
                            locationLinkTextColorClasses,
                            locationLinkBorderColorClasses,
                        ]"
                    >
                        <ExternalLinkIcon class="size-4" />
                    </RouterLink>
                </template>

                <template #header>{{ props.label }}</template>

                <template #text>
                    {{ $t('open-detail-page') }}
                </template>
            </Tooltip>
        </div>
    </div>
</template>

<script setup lang="ts">
import { nanoid } from 'nanoid';
import { computed, ref, watchEffect } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { RouterLink } from 'vue-router';
import ExternalLinkIcon from '../../Icon/ExternalLinkIcon.vue';
import FlashIcon from '../../Icon/FlashIcon.vue';
import FlashOffIcon from '../../Icon/FlashOffIcon.vue';
import TerrainIcon from '../../Icon/TerrainIcon.vue';
import WarningIcon from '../../Icon/WarningIcon.vue';
import Tooltip from '../../Tooltip/Tooltip.vue';

type HeaderFirstColumnCellProps = {
    label: string;
    conflictsCount: {
        resolved: number;
        unresolved: number;
        total: number;
    };
    errorMessage?: string;
    linkTarget?: RouteLocationRaw;
    checkboxStatus:
        | 'Checked'
        | 'Partially-Checked'
        | 'Not-Checked'
        | 'Disabled-Checked'
        | 'Disabled-Not-Checked'
        | 'No-Checkbox';
    checkboxTooltip?: string;
};

const props = defineProps<HeaderFirstColumnCellProps>();

defineEmits<{
    (e: 'toggleLocationExpanded'): void;
    (e: 'keyPress', event: KeyboardEvent): void;
    (e: 'toggle-checkbox'): void;
}>();

const checkBoxRef = ref<HTMLInputElement>();
const labelId = nanoid();

watchEffect(() => {
    if (checkBoxRef.value) {
        checkBoxRef.value.indeterminate = props.checkboxStatus === 'Partially-Checked';
    }
});

// The site header is the top-level container and has no expand/collapse, so unresolved conflicts
// always render as the "light" red treatment (never the collapsed "strong" dark red of location rows).
const hasUnresolvedConflicts = computed(() => props.conflictsCount.unresolved > 0);

// Only show the "all resolved" icon once every conflict is resolved.
const showResolvedConflictsIcon = computed(
    () => props.conflictsCount.total > 0 && props.conflictsCount.unresolved === 0,
);

const backgroundClass = computed(() => {
    if (hasUnresolvedConflicts.value) {
        return 'bg-red-100';
    }

    return 'bg-gray-50';
});

const textColorClass = computed(() => {
    if (props.errorMessage) {
        return 'text-red-700';
    }

    return 'text-gray-900';
});

const borderTopClass = computed(() => {
    if (hasUnresolvedConflicts.value) {
        return 'header-first-column-cell-border-top header-first-column-cell-border-top-1 header-first-column-cell-border-top-red-200';
    }

    return 'header-first-column-cell-border-top header-first-column-cell-border-top-1 header-first-column-cell-border-top-gray-200';
});

const borderBottomClass = computed(() => {
    if (hasUnresolvedConflicts.value) {
        return 'header-first-column-cell-border-bottom header-first-column-cell-border-bottom-2 header-first-column-cell-border-bottom-red-200 header-first-column-cell-border-bottom-z-20';
    }

    return 'header-first-column-cell-border-bottom header-first-column-cell-border-bottom-2 header-first-column-cell-border-bottom-gray-300';
});

const borderRightClass = computed(() => {
    if (hasUnresolvedConflicts.value) {
        return 'header-first-column-cell-border-right header-first-column-cell-border-right-2-max-height header-first-column-cell-border-right-red-800 header-first-column-cell-border-right-z-40';
    }
});

const locationLinkBackgroundColorClasses = computed(() => {
    if (hasUnresolvedConflicts.value) {
        return 'bg-gray-50 hover:bg-red-700 active:bg-red-900';
    }

    return 'bg-gray-50 hover:bg-gray-700 active:bg-gray-900';
});

const locationLinkTextColorClasses = computed(() => {
    return 'text-gray-700 hover:text-gray-50 active:text-gray-50';
});

const locationLinkBorderColorClasses = computed(() => {
    if (hasUnresolvedConflicts.value) {
        return 'border-red-300 hover:border-red-800 active:border-red-900';
    }

    return 'border-gray-300 hover:border-gray-800 active:border-gray-900';
});
</script>

<style scoped>
/**
 * These are needed to avoid ugly triangular intersections of 1px and 2px wide
 * borders which can happen due to border-collapse: separate; being used.
 * border-collapse: separate; is required to implement sticky columns/rows
 * without losing the border when cells are scrolled under the sticky ones.
 */

.header-first-column-cell-border-top,
.header-first-column-cell-border-bottom,
.header-first-column-cell-border-right {
    position: relative;
}

.header-first-column-cell-border-top-1::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 100%;
    height: 1px;
    z-index: 10;
}
.header-first-column-cell-border-top-red-200::before {
    background-color: var(--color-red-200);
}
.header-first-column-cell-border-top-gray-200::before {
    background-color: var(--color-gray-200);
}

.header-first-column-cell-border-bottom-2::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
}
.header-first-column-cell-border-bottom-red-200::after {
    background-color: var(--color-red-200);
}
.header-first-column-cell-border-bottom-gray-300::after {
    background-color: var(--color-gray-300);
}
.header-first-column-cell-border-bottom-z-20::after {
    z-index: 20;
}

.header-first-column-cell-border-right-2-max-height::after {
    content: '';
    position: absolute;
    top: -1px;
    right: -2px;
    width: 2px;
    height: calc(100% + 2px);
}
.header-first-column-cell-border-right-red-800::after {
    background-color: var(--color-red-800);
}
.header-first-column-cell-border-right-z-40::after {
    z-index: 40;
}
</style>
